import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, doc, getDoc, query, orderBy } from 'firebase/firestore';
import { db, teacherDisplayName } from '../firebase';
import { VIRTUES, VIRTUE_SENTENCES, OPENINGS, CLOSINGS, MIXED_OPENINGS, getOpenings, getBridgingSentences, isMixedPerformance } from '../data/virtueData';

const VIRTUE_LONG = { D: 'discipline', A: 'attention', C: 'charity', I: 'inquiry' };

function parseStudentScores(allScores, studentName) {
  const result = {};
  for (const [dateStr, dateData] of Object.entries(allScores || {})) {
    const sd = dateData?.[studentName];
    if (sd) {
      result[dateStr] = {};
      for (const [k, v] of Object.entries(sd)) {
        if (VIRTUE_LONG[k]) result[dateStr][VIRTUE_LONG[k]] = v;
        else if (k === 'E') result[dateStr].absent = v;
      }
    }
  }
  return result;
}

// Rebuild narrative from saved narrative config data
function rebuildNarrative(studentData, className) {
  if (!studentData || !studentData.scores || !studentData.sentenceSelections) return null;
  const { name, pronoun, scores, sentenceSelections, openingIndex, closingIndex } = studentData;
  
  const vals = VIRTUES.map(v => scores[v.key]).filter(s => s !== null && s !== undefined);
  if (vals.length < 4) return null;
  const overall = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  
  if (openingIndex === null || openingIndex === undefined) return null;
  if (closingIndex === null || closingIndex === undefined) return null;
  for (const v of VIRTUES) {
    if (sentenceSelections[v.key] === null || sentenceSelections[v.key] === undefined) return null;
  }

  const pr = pronoun || 'he';
  const displayName = name || '[Student]';
  const displayClass = className || '[Class]';

  const openings = getOpenings(overall, scores);
  const opening = (openings[openingIndex] || '')
    .replace('[S]', displayName)
    .replace('[C]', displayClass);

  const virtueParts = VIRTUES.map(v => {
    const score = scores[v.key];
    const idx = sentenceSelections[v.key];
    return VIRTUE_SENTENCES[v.key]?.[score]?.[pr]?.[idx] || '';
  });

  const bridges = getBridgingSentences(scores, pr);
  const closingOptions = [...(CLOSINGS[overall]?.[pr] || []), ...(CLOSINGS[overall]?.n || [])];
  const closing = closingOptions[closingIndex] || '';

  return [opening, ...virtueParts, ...bridges, closing].filter(Boolean).join(' ');
}

export default function StudentReport({ studentName, onClose }) {
  const [housePoints, setHousePoints] = useState([]);
  const [conductEntries, setConductEntries] = useState([]);
  const [classData, setClassData] = useState([]);
  const [narratives, setNarratives] = useState([]);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // House points (includes merits and demerits)
      const hpSnap = await getDocs(query(collection(db, 'housePointEntries'), orderBy('createdAt', 'desc')));
      const allEntries = hpSnap.docs.map(d => d.data()).filter(e => e.studentName?.toLowerCase() === studentName.toLowerCase());
      setHousePoints(allEntries);
      setConductEntries(allEntries);

      // Participation + narratives
      const teacherIds = ['RfcdU5sf2Zhzj4aJTbfE7Iy5e5E2', 'hvThHfEBFAY7VrG3YQ3djt0Icxi1', 'xn858oNYT3XOP6afwXh9qnT06cx2'];
      const classes = [];
      const foundNarratives = [];

      for (const uid of teacherIds) {
        try {
          // Load class participation data
          const classSnap = await getDocs(collection(db, 'teachers', uid, 'classes'));
          for (const cDoc of classSnap.docs) {
            const raw = cDoc.data();
            if ((raw.roster || []).some(n => n.toLowerCase() === studentName.toLowerCase())) {
              const actualName = raw.roster.find(n => n.toLowerCase() === studentName.toLowerCase());
              const scores = parseStudentScores(raw.scores, actualName);
              const virtueAvgs = {};
              VIRTUES.forEach(v => {
                const vals = [];
                for (const ds of Object.values(scores)) { if (ds[v.key]) vals.push(ds[v.key]); }
                virtueAvgs[v.key] = vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length) : null;
              });
              const allDayAvgs = [];
              for (const ds of Object.values(scores)) {
                if (ds.absent) continue;
                const avg = VIRTUES.reduce((s, v) => s + (ds[v.key] || 0), 0) / VIRTUES.length;
                if (avg > 0) allDayAvgs.push(avg);
              }
              let trend = 'Steady';
              if (allDayAvgs.length >= 4) {
                const mid = Math.floor(allDayAvgs.length / 2);
                const firstHalf = allDayAvgs.slice(0, mid).reduce((a, b) => a + b, 0) / mid;
                const secondHalf = allDayAvgs.slice(mid).reduce((a, b) => a + b, 0) / (allDayAvgs.length - mid);
                if (secondHalf - firstHalf >= 0.3) trend = 'Improving';
                else if (firstHalf - secondHalf >= 0.3) trend = 'Declining';
              }
              classes.push({
                name: raw.cls || raw.name || 'Unnamed',
                teacher: teacherDisplayName(uid),
                daysScored: allDayAvgs.length,
                overallAvg: allDayAvgs.length > 0 ? (allDayAvgs.reduce((a, b) => a + b, 0) / allDayAvgs.length) : null,
                virtueAvgs,
                trend,
              });
            }
          }

          // Load narrative config for this teacher
          try {
            const narrativeSnap = await getDoc(doc(db, 'teachers', uid, 'config', 'narrative'));
            if (narrativeSnap.exists()) {
              const config = narrativeSnap.data();
              const stuNarrative = (config.students || []).find(
                s => s.name?.toLowerCase() === studentName.toLowerCase()
              );
              if (stuNarrative) {
                const text = rebuildNarrative(stuNarrative, config.className);
                if (text) {
                  foundNarratives.push({
                    className: config.className || 'Unknown Class',
                    teacher: teacherDisplayName(uid),
                    text: text + (stuNarrative.comment ? ' ' + stuNarrative.comment : ''),
                  });
                }
              }
            }
          } catch (e) { /* no narrative config */ }
        } catch (e) { /* skip teacher */ }
      }
      setClassData(classes);
      setNarratives(foundNarratives);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [studentName]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalHP = housePoints.reduce((s, e) => s + (e.points || 0), 0);
  const merits = conductEntries.filter(e => e.type === 'merit' || (!e.type && e.points > 0));
  const demerits = conductEntries.filter(e => e.type === 'demerit' || (!e.type && e.points < 0));

  const handlePrint = () => {
    const content = reportRef.current;
    if (!content) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Student Report — ${studentName}</title>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        body { font-family: 'DM Sans', sans-serif; color: #1F2937; padding: 0; margin: 0; }
        .letterhead {
          background: linear-gradient(135deg, #1B3A5C 0%, #0F2440 100%);
          color: #fff;
          padding: 24px 40px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .letterhead img { height: 50px; }
        .letterhead .school-info { text-align: right; }
        .letterhead .school-name { font-family: 'Libre Baskerville', serif; font-size: 14px; font-weight: 700; color: #C9A227; }
        .letterhead .school-detail { font-size: 10px; opacity: 0.7; margin-top: 2px; }
        .report-body { padding: 24px 40px 40px; max-width: 800px; }
        h1 { font-family: 'Libre Baskerville', serif; color: #1B3A5C; font-size: 22px; margin: 0 0 2px; }
        .report-subtitle { font-size: 13px; color: #6B7280; margin-bottom: 20px; }
        h2 { font-family: 'Libre Baskerville', serif; color: #1B3A5C; font-size: 14px; margin-top: 24px; margin-bottom: 8px; border-bottom: 2px solid #1B3A5C; padding-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; font-size: 12px; }
        th { text-align: left; padding: 6px 8px; background: #F3F4F6; border-bottom: 2px solid #D1D5DB; font-size: 10px; text-transform: uppercase; color: #6B7280; letter-spacing: 0.5px; }
        td { padding: 6px 8px; border-bottom: 1px solid #E5E7EB; }
        .stats-row { display: flex; gap: 12px; margin-bottom: 20px; }
        .stat-box { flex: 1; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; padding: 10px; text-align: center; }
        .stat-val { font-family: 'Libre Baskerville', serif; font-size: 20px; font-weight: 700; color: #1B3A5C; }
        .stat-lbl { font-size: 10px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.5px; }
        .stat-green .stat-val { color: #16A34A; }
        .stat-red .stat-val { color: #DC2626; }
        .narrative-block { background: #FAF8F2; border-left: 3px solid #C9A227; padding: 14px 16px; margin-bottom: 10px; font-family: 'Libre Baskerville', serif; font-size: 12px; line-height: 1.9; color: #374151; }
        .narrative-label { font-family: 'DM Sans', sans-serif; font-size: 10px; font-weight: 600; color: #6B7280; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; }
        .merit-badge { color: #16A34A; font-weight: 600; }
        .demerit-badge { color: #DC2626; font-weight: 600; }
        .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #E5E7EB; font-size: 10px; color: #9CA3AF; text-align: center; }
        @media print {
          body { padding: 0; }
          .letterhead { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .stat-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .narrative-block { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      </style>
    </head><body>
      <div class="letterhead">
        <img src="https://static.wixstatic.com/media/f61363_bf5fe629d9c14c2380f88ea0a522389f~mv2.png/v1/fill/w_666,h_158,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Copy%20of%20CSN_CAFM_AltLogo_FULL.png" alt="CAFM Logo" />
        <div class="school-info">
          <div class="school-name">Chesterton Academy of the Florida Martyrs</div>
          <div class="school-detail">A Classical Catholic High School · Pensacola, Florida</div>
        </div>
      </div>
      <div class="report-body">
        ${content.innerHTML}
        <div class="footer">
          Chesterton Academy of the Florida Martyrs · Family Life Center, St. Anne Catholic Church · Pensacola, FL<br/>
          Report generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500); // Small delay to let logo load
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: 700, padding: 40, textAlign: 'center' }} onClick={e => e.stopPropagation()}>Loading report...</div>
      </div>
    );
  }

  const thStyle = { textAlign: 'left', padding: '6px 8px', background: '#F9FAFB', borderBottom: '2px solid #E5E7EB', fontSize: 11, textTransform: 'uppercase', color: '#6B7280' };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #F3F4F6' };
  const h2Style = { fontFamily: 'var(--font-display)', fontSize: 15, color: '#1B3A5C', marginTop: 20, marginBottom: 8, borderBottom: '2px solid #E5E7EB', paddingBottom: 4 };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 780, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Student Report</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-gold btn-sm" onClick={handlePrint}>🖨 Print / PDF</button>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div className="modal-body" ref={reportRef}>
          {/* Header */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, color: '#6B7280' }}>Chesterton Academy of the Florida Martyrs</div>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: '#1B3A5C', margin: '4px 0' }}>{studentName}</h1>
            <div style={{ fontSize: 13, color: '#6B7280' }}>Student Report — {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
          </div>

          {/* Summary Stats */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 80, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#1B3A5C' }}>{totalHP}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>House Points</div>
            </div>
            <div style={{ flex: 1, minWidth: 80, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#16A34A' }}>{merits.length}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>Merits</div>
            </div>
            <div style={{ flex: 1, minWidth: 80, background: demerits.length > 0 ? '#FEF2F2' : '#F9FAFB', border: `1px solid ${demerits.length > 0 ? '#FECACA' : '#E5E7EB'}`, borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: demerits.length > 0 ? '#DC2626' : '#6B7280' }}>{demerits.length}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>Demerits</div>
            </div>
            <div style={{ flex: 1, minWidth: 80, background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: '#1B3A5C' }}>{classData.length}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>Classes</div>
            </div>
          </div>

          {/* Participation Narratives — one section per class */}
          <h2 style={h2Style}>Participation Narratives</h2>
          {classData.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 13 }}>No class data found for this student.</div>
          ) : (
            classData.map((cls, i) => {
              const narrative = narratives.find(n => n.teacher === cls.teacher) || narratives.find(n => n.className.toLowerCase() === cls.name.toLowerCase());
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>
                    {cls.name} — {cls.teacher}
                  </div>
                  {narrative ? (
                    <div style={{
                      background: '#FAF8F2', border: '1px solid #E5E7EB', borderRadius: 8,
                      padding: 16, fontFamily: 'var(--font-display)', fontSize: 13, lineHeight: 1.8, color: '#374151'
                    }}>
                      {narrative.text}
                    </div>
                  ) : (
                    <div style={{
                      background: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: 8,
                      padding: 14, fontSize: 12, color: '#9CA3AF', fontStyle: 'italic'
                    }}>
                      No narrative written yet for this class. The teacher can build one in the Narrative Builder.
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Participation Scores */}
          <h2 style={h2Style}>Participation Scores</h2>
          {classData.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 13 }}>No participation data found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={thStyle}>Class</th>
                  <th style={thStyle}>Teacher</th>
                  <th style={thStyle}>Days</th>
                  {VIRTUES.map(v => <th key={v.key} style={{ ...thStyle, color: v.color }}>{v.label.substring(0, 4)}</th>)}
                  <th style={thStyle}>Avg</th>
                  <th style={thStyle}>Grade</th>
                  <th style={thStyle}>Trend</th>
                </tr>
              </thead>
              <tbody>
                {classData.map((c, i) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, fontWeight: 500 }}>{c.name}</td>
                    <td style={tdStyle}>{c.teacher}</td>
                    <td style={tdStyle}>{c.daysScored}</td>
                    {VIRTUES.map(v => (
                      <td key={v.key} style={tdStyle}>{c.virtueAvgs[v.key] !== null ? c.virtueAvgs[v.key].toFixed(1) : '—'}</td>
                    ))}
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{c.overallAvg !== null ? c.overallAvg.toFixed(2) : '—'}</td>
                    <td style={tdStyle}>{c.overallAvg !== null ? Math.round((c.overallAvg / 5) * 100) + '%' : '—'}</td>
                    <td style={{ ...tdStyle, fontWeight: 500, color: c.trend === 'Improving' ? '#16A34A' : c.trend === 'Declining' ? '#DC2626' : '#6B7280' }}>
                      {c.trend === 'Improving' ? '↑' : c.trend === 'Declining' ? '↓' : '→'} {c.trend}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* House Points Detail */}
          <h2 style={h2Style}>House Points ({totalHP})</h2>
          {housePoints.length === 0 ? (
            <div style={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: 13 }}>No house points recorded.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: 13 }}>
              <thead><tr>
                <th style={thStyle}>Date</th><th style={thStyle}>Type</th><th style={thStyle}>Category</th><th style={thStyle}>Points</th><th style={thStyle}>Reason</th>
              </tr></thead>
              <tbody>
                {housePoints.map((e, i) => {
                  const isMerit = e.type === 'merit' || (!e.type && e.points > 0);
                  return (
                    <tr key={i}>
                      <td style={tdStyle}>{e.createdAt ? new Date(e.createdAt).toLocaleDateString() : '—'}</td>
                      <td style={tdStyle}><span style={{ color: isMerit ? '#16A34A' : '#DC2626', fontWeight: 600, fontSize: 12 }}>{isMerit ? '★ Merit' : '✗ Demerit'}</span></td>
                      <td style={tdStyle}>{e.category || '—'}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: isMerit ? '#16A34A' : '#DC2626' }}>{e.points > 0 ? '+' : ''}{e.points}</td>
                      <td style={tdStyle}>{e.reason}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
